// Batch G — Intelligence Hero strip.
//
// Renders the portfolio-wide rollups from GET /api/intelligence/summary:
//
//   ┌────────────────┬────────────────┬────────────────┬──────────────────┐
//   │ TOTAL DEALS    │ ASKING TOTAL   │ COUNTER TOTAL  │ POTENTIAL SAVING │
//   │ 12             │ $3.84M         │ $3.51M         │ $330k (8.6%)     │
//   ├────────────────┼────────────────┼────────────────┼──────────────────┤
//   │ CF @ ASKING    │ CF @ COUNTER   │ AVG CAP RATE   │ VERDICT SHIFTS   │
//   │ -$1,200/mo     │ +$2,400/mo     │ 5.4% → 6.8%    │ 7↑ 4= 1↓         │
//   └────────────────┴────────────────┴────────────────┴──────────────────┘
//
// Surface is "Stripe-grade KPI strip" — soft borders, generous spacing, no
// gradients, no playful chrome.

import { ArrowDown, ArrowUp, Info, Minus, TrendingDown, TrendingUp } from "lucide-react";
import { useFmt } from "@/lib/useFmt";

// Phase 43 Batch BE — Group 5. Friendly tooltip explaining why a deal's
// verdict can shift up without changing CF (cap-rate-driven shifts on
// cash purchases). Without this, the user-reported "11/11 deals show
// $0 CF delta but verdict shifts 6↑" looks like a bug — Phase 42
// Finding C.
const VERDICT_SHIFTS_TOOLTIP =
  "Verdict shifts are driven by cap rate, cash-on-cash, AND cash flow. " +
  "Cash purchases can improve verdict via cap rate alone, with no CF change.";

function KpiCard({ label, value, sub, accent = "default", testId, hint }) {
  const accentBg = {
    default: "bg-white",
    positive: "bg-emerald-50/40",
    negative: "bg-rose-50/40",
    indigo: "bg-indigo-50/40",
  }[accent];
  const accentText = {
    default: "text-slate-900",
    positive: "text-emerald-700",
    negative: "text-rose-700",
    indigo: "text-indigo-700",
  }[accent];
  return (
    <div
      data-testid={testId}
      className={`flex flex-col gap-1 rounded-xl border border-slate-200 ${accentBg} px-4 py-3 shadow-sm`}
    >
      <p className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
        {hint ? (
          <span
            title={hint}
            data-testid={`${testId}-hint`}
            className="cursor-help text-slate-400 hover:text-slate-600"
          >
            <Info className="h-3 w-3" />
          </span>
        ) : null}
      </p>
      <p className={`text-xl font-semibold tabular-nums tracking-tight ${accentText}`}>
        {value}
      </p>
      {sub ? <p className="text-xs text-slate-500">{sub}</p> : null}
    </div>
  );
}

// Phase 43 Batch BE — Group 5. Resolve a friendly label for the
// current Stage filter so the "Properties analysed" sub-copy reflects
// the user's scope ("in Active deals" instead of the misleading "in
// your book" — Phase 42 Finding E follow-up).
const STAGE_LABELS = {
  all_active: "Active deals",
  all_excl_passed: "All except passed",
  all: "All deals",
  pipeline: "Pipeline",
  negotiating: "Negotiating",
  under_contract: "Under contract",
  owned: "Owned",
  sold: "Sold",
  passed: "Passed",
};

export default function IntelligenceHero({ summary, stageFilter }) {
  const fmt = useFmt();
  if (!summary) return null;

  const t = summary.totals || {};
  const a = summary.averages || {};
  const v = summary.verdict_shifts || {};

  const savingsPositive = (t.total_savings_dollar || 0) > 0;
  const cfDeltaPositive =
    (t.total_monthly_cash_flow_at_counter || 0) >=
    (t.total_monthly_cash_flow_at_asking || 0);
  // Phase 43 Batch BE — Group 5. When CF @ asking == CF @ counter
  // within $1, surface a subscript explaining "your portfolio has
  // no debt-service delta on the counter-applied deals" (Phase 42
  // Finding A UX gap — math is correct for cash purchases but the
  // user perceives identical-to-the-cent values as a bug).
  const cfIdentical = Math.abs(
    (t.total_monthly_cash_flow_at_counter || 0) -
      (t.total_monthly_cash_flow_at_asking || 0),
  ) < 1;
  const capDelta = (a.cap_rate_at_counter || 0) - (a.cap_rate_at_asking || 0);

  // Friendly label for "in <scope>" sub-copy.
  const scopeLabel =
    stageFilter && stageFilter !== "all"
      ? STAGE_LABELS[stageFilter] || stageFilter
      : null;

  return (
    <section
      data-testid="intelligence-hero"
      className="grid grid-cols-2 gap-3 sm:grid-cols-4"
    >
      <KpiCard
        testId="kpi-deal-count"
        label="Properties analysed"
        value={t.deal_count ?? 0}
        sub={
          summary.capped
            ? "capped at 200"
            : scopeLabel
              ? `in ${scopeLabel}`
              : "in your book"
        }
      />
      <KpiCard
        testId="kpi-asking-total"
        label="Total asking"
        value={fmt.money(t.total_asking_value || 0, { primaryOnly: true })}
      />
      <KpiCard
        testId="kpi-counter-total"
        label="Total counter"
        value={fmt.money(t.total_counter_value || 0, { primaryOnly: true })}
        sub="across all deals"
      />
      <KpiCard
        testId="kpi-savings"
        label="Potential saving"
        accent={savingsPositive ? "positive" : "default"}
        value={
          <span className="inline-flex items-baseline gap-1.5">
            {fmt.money(t.total_savings_dollar || 0, { primaryOnly: true })}
            {savingsPositive ? (
              <ArrowDown className="h-4 w-4 self-center text-emerald-600" />
            ) : null}
          </span>
        }
        sub={`${(t.total_savings_percent || 0).toFixed(1)}% off asking`}
      />
      <KpiCard
        testId="kpi-cf-asking"
        label="Monthly CF @ asking"
        accent={(t.total_monthly_cash_flow_at_asking || 0) < 0 ? "negative" : "default"}
        value={fmt.money(t.total_monthly_cash_flow_at_asking || 0, { primaryOnly: true, cents: true })}
        sub={cfIdentical ? "(no debt-service delta across deals)" : undefined}
      />
      <KpiCard
        testId="kpi-cf-counter"
        label="Monthly CF @ counter"
        accent={cfDeltaPositive ? "positive" : "negative"}
        value={
          <span className="inline-flex items-baseline gap-1.5">
            {fmt.money(t.total_monthly_cash_flow_at_counter || 0, { primaryOnly: true, cents: true })}
            {cfDeltaPositive ? (
              <TrendingUp className="h-4 w-4 self-center text-emerald-600" />
            ) : (
              <TrendingDown className="h-4 w-4 self-center text-rose-600" />
            )}
          </span>
        }
        sub={
          cfIdentical
            ? "(no debt-service delta across deals)"
            : `${cfDeltaPositive ? "+" : ""}${fmt.money(
                (t.total_monthly_cash_flow_at_counter || 0) - (t.total_monthly_cash_flow_at_asking || 0),
                { primaryOnly: true, cents: true }
              )} vs asking`
        }
      />
      <KpiCard
        testId="kpi-cap"
        label="Avg cap rate"
        accent={capDelta > 0 ? "positive" : "default"}
        value={
          <span className="inline-flex items-baseline gap-1.5">
            {(a.cap_rate_at_counter ?? 0).toFixed(2)}%
            {capDelta > 0 ? (
              <ArrowUp className="h-4 w-4 self-center text-emerald-600" />
            ) : capDelta < 0 ? (
              <ArrowDown className="h-4 w-4 self-center text-rose-600" />
            ) : (
              <Minus className="h-4 w-4 self-center text-slate-400" />
            )}
          </span>
        }
        sub={`asking ${(a.cap_rate_at_asking ?? 0).toFixed(2)}%`}
      />
      <KpiCard
        testId="kpi-verdict-shifts"
        label="Verdict shifts"
        accent="indigo"
        hint={VERDICT_SHIFTS_TOOLTIP}
        value={
          <span className="inline-flex items-center gap-2 text-base">
            <span className="inline-flex items-center gap-0.5 text-emerald-700">
              <ArrowUp className="h-4 w-4" /> {v.improved ?? 0}
            </span>
            <span className="inline-flex items-center gap-0.5 text-slate-500">
              <Minus className="h-4 w-4" /> {v.same ?? 0}
            </span>
            <span className="inline-flex items-center gap-0.5 text-rose-700">
              <ArrowDown className="h-4 w-4" /> {v.degraded ?? 0}
            </span>
          </span>
        }
        sub="if counters accepted"
      />
    </section>
  );
}
